import React from 'react';
import { Box, CircularProgress, Modal, Typography, Button } from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useAuth from './hooks/useAuth';
import useIdleTimer from './hooks/useIdleTimer';
import LoginPage from './pages/LoginPage';
import CopilotPage from './pages/CopilotPage';
import DataIngestionPage from './pages/DataIngestionPage';
import AdminPage from './pages/AdminPage';
import CopilotPageWrapper from './pages/CopilotPageWrapper';

// ProtectedRoute component checks if a user is logged in and has the correct role.
const ProtectedRoute = ({ isLoggedIn, userRole, allowedRoles, children }) => {
    if (!isLoggedIn) {
        return <Navigate to="/login" replace />;
    }
    if (allowedRoles && !allowedRoles.includes(userRole)) {
        return <Navigate to="/" replace />;
    }
    return children;
};

// Main application component
export default function App() {
    const { isLoggedIn, userRole, login, logout, isLoading } = useAuth();

    // Idle timer setup
    const { isPromptVisible, stayActive } = useIdleTimer({
        onIdle: logout,
        idleTimeout: 30 * 60 * 1000, // 30 minutes
        promptTimeout: 60 * 1000     // 1 minute warning
    });

    // Show a loading spinner while the initial session is being checked
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <BrowserRouter>
                <Routes>
                    {/* <Route path="/login" element={<LoginPage onLogin={login} />} /> */}
                    <Route path="/login" element={<LoginPage />} />

                    {/* Main copilot page, accessible to all logged-in users */}
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute isLoggedIn={isLoggedIn} userRole={userRole}>
                                <CopilotPage onLogout={logout} />
                            </ProtectedRoute>
                        }
                    />

                    {/* Route to handle incoming redirects with URL parameters */}
                    <Route
                        path="/page"
                        element={
                            <ProtectedRoute isLoggedIn={isLoggedIn} userRole={userRole}>
                                <CopilotPageWrapper onLogout={logout} />
                            </ProtectedRoute>
                        }
                    />

                    {/* Admin-only routes */}
                    <Route
                        path="/data-ingestion"
                        element={
                            <ProtectedRoute isLoggedIn={isLoggedIn} userRole={userRole} allowedRoles={['admin']}>
                                <DataIngestionPage onLogout={logout} />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin-page"
                        element={
                            <ProtectedRoute isLoggedIn={isLoggedIn} userRole={userRole} allowedRoles={['admin']}>
                                <AdminPage onLogout={logout} />
                            </ProtectedRoute>
                        }
                    />

                    {/* Catch-all route to redirect users */}
                    <Route
                        path="*"
                        element={<Navigate to={isLoggedIn ? (userRole === 'admin' ? '/admin-page' : '/') : "/login"} />}
                    />
                </Routes>
            </BrowserRouter>

            {/* Idle Timeout Warning Modal */}
            <Modal open={isPromptVisible}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2
                }}>
                    <Typography variant="h6" component="h2">Session Timeout</Typography>
                    <Typography sx={{ mt: 2 }}>You will be logged out in 1 minute due to inactivity.</Typography>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button variant="outlined" onClick={logout}>Logout Now</Button>
                        <Button variant="contained" onClick={stayActive}>Stay Logged In</Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}